import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';

import { useI18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { subscribeInboxes, type InboxEntry } from '../lib/repositories/inbox-repo.js';
import { useInboxPaths } from '../lib/use-inbox-paths.js';

/**
 * Dashboard inbox bell — surfaces `users/{uid}/inbox` (plus the brand admin
 * queue for a super-admin) (the operator's Daily
 * Money Moment digest, first-win celebration, streak-loss nudge) and its unread
 * count. Rendered inside AppNav so it rides along with the existing nav chrome
 * on every screen the nav appears on — no new floating corner.
 *
 * Bounded listener (limit N) cleaned up by useTrackedSnapshot; the badge fails
 * quietly (the /inbox reader owns the visible error state). i18n aria-label.
 */
export function InboxBell() {
  const { t } = useI18n();
  const paths = useInboxPaths();
  const subscribe = useMemo(() => {
    if (!paths) return null;
    return (cb: (vs: InboxEntry[]) => void) => subscribeInboxes(paths, cb, () => undefined);
  }, [paths]);

  const { data } = useTrackedSnapshot<InboxEntry[]>(
    ['inbox-bell', paths?.join('|') ?? null],
    subscribe,
  );
  const unread = (data ?? []).filter((e) => !e.message.readAt).length;
  const label =
    unread > 0
      ? t('dashboard.inbox.bellAriaUnread', { count: unread })
      : t('dashboard.inbox.bellAria');

  return (
    <NavLink to="/inbox" aria-label={label} style={bellStyle}>
      <BellIcon />
      {unread > 0 ? (
        <span aria-hidden style={badgeStyle}>
          {unread > 9 ? '9+' : unread}
        </span>
      ) : null}
    </NavLink>
  );
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M13.7 21a2 2 0 0 1-3.4 0"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

const bellStyle: React.CSSProperties = {
  position: 'relative',
  minWidth: 'var(--touch-target)',
  minHeight: 'var(--touch-target)',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-strong)',
  background: 'var(--color-surface)',
  color: 'var(--color-on-surface)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  flexShrink: 0,
};

const badgeStyle: React.CSSProperties = {
  position: 'absolute',
  top: 2,
  right: 2,
  minWidth: 16,
  height: 16,
  padding: '0 4px',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  fontSize: '0.625rem',
  fontWeight: 700,
  lineHeight: '16px',
  textAlign: 'center',
  fontVariantNumeric: 'tabular-nums',
};
